'use client';

import { useQuery } from '@tanstack/react-query';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { useProjectStore } from '../../store/projectStore';
import { getAuthHeaders } from '../../utils/authHeaders';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';

export type ApplicationCredentialListItem = {
  id: string;
  username?: string;
  role?: string;
  isDisabled?: boolean;
};

type UseApplicationCredentialsListOptions = {
  applicationId?: string;
  enabled?: boolean;
};

export function useApplicationCredentialsList({
  applicationId,
  enabled = true,
}: UseApplicationCredentialsListOptions) {
  const selectedCompany = useProjectStore((state) => state.selectedCompany);
  const tenantId = selectedCompany?.tenantId;

  return useQuery({
    queryKey: ['application-credentials', applicationId, tenantId],
    queryFn: async (): Promise<{
      credentials: ApplicationCredentialListItem[];
    }> => {
      if (!applicationId || !tenantId) {
        return { credentials: [] };
      }

      const authHeaders = await getAuthHeaders();
      const response = await applicationsClient.getCredentials({
        params: { id: applicationId },
        query: { includeDisabled: true },
        overrideClientOptions: {
          baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
        },
        extraHeaders: {
          ...authHeaders,
        },
      });

      if (response.status !== 200) {
        throw new Error('Failed to fetch credentials');
      }

      return {
        credentials: response.body.credentials.map((credential) => ({
          id: credential.id,
          username: credential.username,
          role: credential.role,
          isDisabled: credential.isDisabled,
        })),
      };
    },
    enabled: !!applicationId && !!tenantId && enabled,
    refetchOnWindowFocus: false,
  });
}
